import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import QRCode from 'qrcode'
import { ApiError } from '@akillidernek/api-client'
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Field,
  Icon,
  Input,
  useToast,
} from '@akillidernek/ui'
import { api } from '../api'

/**
 * Güvenlik — ikinci adım doğrulama (YES-115).
 *
 * Bu ekran DERNEĞE değil KİŞİYE ait: bir kişi birden fazla derneğe bağlı
 * olabilir (ADR-1) ve ikinci adımı bir kez açar. Bu yüzden Ayarlar
 * sekmelerinin altında değil, ayrı bir sayfada.
 */
export function Guvenlik() {
  const durum = useQuery({
    queryKey: ['mfa'],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/auth/mfa')
      return data
    },
  })

  /** Kurulum başladıysa sır ve QR burada; kapanınca temizlenir. */
  const [kurulum, setKurulum] = useState<{ secret: string; otpauth_uri: string } | null>(null)
  /** Yalnızca bir kez gösterilecek kurtarma kodları. */
  const [kurtarmaKodlari, setKurtarmaKodlari] = useState<string[] | null>(null)

  return (
    <div className="flex max-w-2xl flex-col gap-4">
      <h1 className="text-lg font-semibold">Güvenlik</h1>

      <Card>
        <CardHeader
          title="İkinci adım doğrulama"
          description="Şifreniz ele geçse bile telefonunuzdaki kod olmadan hesabınıza girilemez."
          action={
            durum.data?.enabled ? (
              <Badge tone="success">Açık</Badge>
            ) : (
              <Badge tone="warning">Kapalı</Badge>
            )
          }
        />
        <CardBody className="flex flex-col gap-4">
          {durum.isLoading && <p className="text-sm text-[var(--ad-text-muted)]">Yükleniyor…</p>}

          {durum.data?.enabled && (
            <Acik
              kalanKod={durum.data.recovery_codes_remaining}
              onKodlar={setKurtarmaKodlari}
              onKapandi={() => {
                setKurulum(null)
                setKurtarmaKodlari(null)
              }}
            />
          )}

          {durum.data && !durum.data.enabled && !kurulum && (
            <Baslat pending={durum.data.pending} onKurulum={setKurulum} />
          )}

          {kurulum && !durum.data?.enabled && (
            <Onayla
              kurulum={kurulum}
              onVazgec={() => setKurulum(null)}
              onAcildi={(kodlar) => {
                setKurulum(null)
                setKurtarmaKodlari(kodlar)
              }}
            />
          )}
        </CardBody>
      </Card>

      {kurtarmaKodlari && (
        <KurtarmaKodlari kodlar={kurtarmaKodlari} onKapat={() => setKurtarmaKodlari(null)} />
      )}
    </div>
  )
}

function Baslat({
  pending,
  onKurulum,
}: {
  pending: boolean
  onKurulum: (kurulum: { secret: string; otpauth_uri: string }) => void
}) {
  const toast = useToast()
  const [hata, setHata] = useState<string | null>(null)

  const basla = useMutation({
    mutationFn: async (password: string) => {
      const { data } = await api.POST('/api/v1/auth/mfa/setup', { body: { password } })
      return data!
    },
    onSuccess: onKurulum,
    onError: (error) => {
      setHata(error instanceof ApiError ? error.problem.detail : 'Kurulum başlatılamadı.')
      toast({ title: 'Kurulum başlatılamadı', tone: 'error' })
    },
  })

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault()
        setHata(null)
        basla.mutate(String(new FormData(event.currentTarget).get('password') ?? ''))
      }}
    >
      {pending && (
        <p className="text-xs text-[var(--ad-text-muted)]">
          Yarım kalmış bir kurulum var. Yeniden başlattığınızda yeni bir kod üretilir; eski QR
          geçersiz olur.
        </p>
      )}

      {/* Şifre isteniyor çünkü çalınmış bir oturum ikinci adımı KENDİ
          telefonuna bağlayabilseydi, 2FA saldırganın lehine çalışırdı. */}
      <Field
        label="Şifreniz"
        required
        hint="Kimliğinizi doğrulamak için. Şifresiz açılmış hesaplarda boş bırakın."
        error={hata ?? undefined}
      >
        {(props) => (
          <Input name="password" type="password" autoComplete="current-password" {...props} />
        )}
      </Field>

      <div>
        <Button type="submit" disabled={basla.isPending}>
          {basla.isPending ? 'Hazırlanıyor…' : 'İkinci adımı kur'}
        </Button>
      </div>
    </form>
  )
}

function Onayla({
  kurulum,
  onVazgec,
  onAcildi,
}: {
  kurulum: { secret: string; otpauth_uri: string }
  onVazgec: () => void
  onAcildi: (kodlar: string[]) => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [qr, setQr] = useState<string | null>(null)
  const [hata, setHata] = useState<string | null>(null)

  useEffect(() => {
    // QR tarayıcıda üretiliyor: sır sunucudan bir kez geldi, ikinci bir uçtan
    // görsel olarak tekrar istemek onu bir kez daha ağa çıkarmak olurdu.
    let iptal = false
    void QRCode.toDataURL(kurulum.otpauth_uri, { margin: 1, width: 220 }).then((veri) => {
      if (!iptal) setQr(veri)
    })
    return () => {
      iptal = true
    }
  }, [kurulum.otpauth_uri])

  const onayla = useMutation({
    mutationFn: async (code: string) => {
      const { data } = await api.POST('/api/v1/auth/mfa/confirm', { body: { code } })
      return data!.codes
    },
    onSuccess: (kodlar) => {
      void queryClient.invalidateQueries({ queryKey: ['mfa'] })
      void queryClient.invalidateQueries({ queryKey: ['me'] })
      toast({ title: 'İkinci adım açıldı', tone: 'success' })
      onAcildi(kodlar)
    },
    onError: (error) =>
      setHata(error instanceof ApiError ? error.problem.detail : 'Kod doğrulanamadı.'),
  })

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault()
        setHata(null)
        onayla.mutate(String(new FormData(event.currentTarget).get('code') ?? ''))
      }}
    >
      <ol className="flex list-decimal flex-col gap-3 pl-5 text-sm">
        <li>
          Google Authenticator, 1Password ya da benzeri bir uygulamada QR kodu okutun.
          <div className="mt-2 flex flex-wrap items-center gap-4">
            {qr ? (
              <img
                src={qr}
                alt="Kimlik doğrulayıcı uygulama için QR kodu"
                className="rounded-[var(--radius-control)] border border-[var(--ad-border)] bg-white p-2"
              />
            ) : (
              <div className="h-[236px] w-[236px] rounded-[var(--radius-control)] bg-[var(--ad-surface-muted)]" />
            )}
            <div className="flex flex-col gap-1">
              <span className="text-xs text-[var(--ad-text-muted)]">
                QR okutamıyorsanız bu anahtarı elle girin:
              </span>
              {/* Elle giriş yolu bilerek duruyor: masaüstünden kurulum yapan
                  ya da kamerası olmayan kullanıcı yoksa 2FA'dan tümden
                  vazgeçerdi. */}
              <code className="select-all rounded-[var(--radius-control)] bg-[var(--ad-surface-muted)] px-2 py-1 text-xs tracking-wider">
                {kurulum.secret}
              </code>
            </div>
          </div>
        </li>
        <li>
          Uygulamanın gösterdiği altı haneli kodu yazın.
          <div className="mt-2 max-w-56">
            <Field label="Doğrulama kodu" required error={hata ?? undefined}>
              {(props) => <Input name="code" autoFocus autoComplete="one-time-code" {...props} />}
            </Field>
          </div>
        </li>
      </ol>

      <p className="text-xs text-[var(--ad-text-muted)]">
        Kod doğrulanana kadar ikinci adım <strong>açılmaz</strong>: QR&apos;ı okutamadıysanız
        hesabınızın dışında kalmazsınız.
      </p>

      <div className="flex gap-2">
        <Button type="submit" disabled={onayla.isPending}>
          {onayla.isPending ? 'Doğrulanıyor…' : 'Doğrula ve aç'}
        </Button>
        <Button type="button" variant="secondary" onClick={onVazgec}>
          Vazgeç
        </Button>
      </div>
    </form>
  )
}

function Acik({
  kalanKod,
  onKodlar,
  onKapandi,
}: {
  kalanKod: number
  onKodlar: (kodlar: string[]) => void
  onKapandi: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [kapatmaAcik, setKapatmaAcik] = useState(false)
  const [hata, setHata] = useState<string | null>(null)

  const tazele = () => {
    void queryClient.invalidateQueries({ queryKey: ['mfa'] })
    void queryClient.invalidateQueries({ queryKey: ['me'] })
  }

  const yenile = useMutation({
    mutationFn: async (password: string) => {
      const { data } = await api.POST('/api/v1/auth/mfa/recovery-codes', { body: { password } })
      return data!.codes
    },
    onSuccess: (kodlar) => {
      tazele()
      toast({ title: 'Yeni kurtarma kodları üretildi', tone: 'success' })
      onKodlar(kodlar)
    },
    onError: (error) =>
      toast({
        title: 'Kodlar yenilenemedi',
        description: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'error',
      }),
  })

  const kapat = useMutation({
    mutationFn: async (form: FormData) => {
      await api.POST('/api/v1/auth/mfa/disable', {
        body: {
          password: String(form.get('password') ?? ''),
          code: String(form.get('code') ?? ''),
        },
      })
    },
    onSuccess: () => {
      tazele()
      setKapatmaAcik(false)
      onKapandi()
      toast({ title: 'İkinci adım kapatıldı', tone: 'success' })
    },
    onError: (error) => setHata(error instanceof ApiError ? error.problem.detail : 'Kapatılamadı.'),
  })

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Icon name="shield-alert" size={18} primaryColor="var(--ad-success)" aria-hidden />
        <span>Girişte telefonunuzdaki kod soruluyor.</span>
        <Badge tone={kalanKod <= 2 ? 'warning' : 'neutral'} className="ml-auto">
          {kalanKod} kurtarma kodu kaldı
        </Badge>
      </div>

      {kalanKod <= 2 && (
        // Kurtarma kodu bitmiş bir kullanıcı telefonunu kaybederse hesabına
        // hiç giremez; bunu sessizce beklemek destek talebine dönüşür.
        <p className="text-xs text-[var(--ad-warning)]">
          Kurtarma kodlarınız tükenmek üzere. Telefonunuzu kaybederseniz hesabınıza girmenin başka
          yolu kalmaz.
        </p>
      )}

      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault()
          yenile.mutate(String(new FormData(event.currentTarget).get('password') ?? ''))
        }}
      >
        <div className="w-56">
          <Field label="Şifreniz" hint="Kurtarma kodlarını yenilemek için">
            {(props) => <Input name="password" type="password" {...props} />}
          </Field>
        </div>
        <Button type="submit" variant="secondary" disabled={yenile.isPending}>
          {yenile.isPending ? 'Üretiliyor…' : 'Kurtarma kodlarını yenile'}
        </Button>
      </form>

      <div className="border-t border-[var(--ad-border)] pt-3">
        {kapatmaAcik ? (
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault()
              setHata(null)
              kapat.mutate(new FormData(event.currentTarget))
            }}
          >
            {/* Hem şifre hem kod isteniyor: yalnızca şifre yetseydi, şifreyi ele
                geçiren biri ikinci adımı tek hamlede kaldırırdı. */}
            <p className="text-sm">Kapatmak için hem şifrenizi hem güncel kodu girin.</p>
            <div className="flex flex-wrap gap-3">
              <div className="w-56">
                <Field label="Şifreniz">
                  {(props) => <Input name="password" type="password" {...props} />}
                </Field>
              </div>
              <div className="w-40">
                <Field label="Doğrulama kodu" required>
                  {(props) => <Input name="code" autoComplete="one-time-code" {...props} />}
                </Field>
              </div>
            </div>
            {hata && (
              <p role="alert" className="text-xs text-[var(--ad-danger)]">
                {hata}
              </p>
            )}
            <div className="flex gap-2">
              <Button type="submit" variant="ghost" disabled={kapat.isPending}>
                {kapat.isPending ? 'Kapatılıyor…' : 'İkinci adımı kapat'}
              </Button>
              <Button type="button" variant="secondary" onClick={() => setKapatmaAcik(false)}>
                Vazgeç
              </Button>
            </div>
          </form>
        ) : (
          <Button type="button" variant="ghost" onClick={() => setKapatmaAcik(true)}>
            İkinci adımı kapat
          </Button>
        )}
      </div>
    </div>
  )
}

function KurtarmaKodlari({ kodlar, onKapat }: { kodlar: string[]; onKapat: () => void }) {
  return (
    <Card>
      <CardHeader
        title="Kurtarma kodları"
        description="Bu kodlar BİR KEZ gösterilir. Sunucuda yalnızca özetleri saklanıyor; bu ekranı kapattıktan sonra bir daha görüntülenemezler."
      />
      <CardBody className="flex flex-col gap-3">
        <ul className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
          {kodlar.map((kod) => (
            <li
              key={kod}
              className="select-all rounded-[var(--radius-control)] bg-[var(--ad-surface-muted)] px-2 py-1.5 text-center font-mono text-sm tracking-wider"
            >
              {kod}
            </li>
          ))}
        </ul>
        <p className="text-xs text-[var(--ad-text-muted)]">
          Her kod bir kez çalışır. Telefonunuzu kaybederseniz hesabınıza girmenin tek yolu bunlar.
        </p>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => void navigator.clipboard?.writeText(kodlar.join('\n'))}
          >
            Kopyala
          </Button>
          <Button type="button" onClick={onKapat}>
            Kaydettim, kapat
          </Button>
        </div>
      </CardBody>
    </Card>
  )
}
